import React, { FC, ReactElement } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import classnames from 'classnames';
import { compact } from 'lodash';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import Link from 'buyside/components/presentation/Link/link';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import StarIcon from 'buyside/components/presentation/Star/star';

import DownArrow from 'SVGs/triangle_down.svg';
import UpArrow from 'SVGs/triangle_up.svg';
import LightbulbIcon from 'SVGs/lightbulb.svg';

import { formatDate } from 'buyside/util/dateHelper';
import { ideasUrl, ideaUrl } from 'buyside/util/urlHelper';
import useInfiniteScroll from 'buyside/util/hooks/useInfiniteScroll';

import {
  Maybe,
  CurrentUserRatingsQuery,
  useCurrentUserRatingsQuery
} from 'interfaces/graphql-typings';

import styles from './userRatingsPageStyles.scss';

const calculateFill = (idx: number, halfScore: number): number => {
  if (halfScore >= idx + 1) {
    return 100;
  } else if (halfScore > idx) {
    return (halfScore - idx) * 100;
  } else {
    return 0;
  }
};

const renderReturn = (percentage: Maybe<number>): ReactElement => {
  const ArrowIcon = percentage && (percentage < 0 ? DownArrow : UpArrow);

  if (!percentage) return <span className={styles.pendingText}>N/A</span>;

  return (
    <span
      className={classnames({
        [styles.returnsPositive]: percentage >= 0,
        [styles.returnsNegative]: percentage < 0
      })}
    >
      <ArrowIcon className={styles.arrowIcon} />
      {percentage}%
    </span>
  );
};

const UserRatingsPage: FC = (): ReactElement => {
  const { data, loading, fetchMore } = useCurrentUserRatingsQuery();
  const edges = data?.currentUser?.ratings?.edges;
  const pageInfo = data?.currentUser?.ratings?.pageInfo;

  const [isFetching] = useInfiniteScroll(
    Boolean(pageInfo?.hasNextPage),
    (): Promise<ApolloQueryResult<CurrentUserRatingsQuery>> =>
      fetchMore({ variables: { cursor: pageInfo?.endCursor } })
  );

  return (
    <>
      <div className={styles.pageTitle}>Your Ratings</div>
      {loading ? <LoadingSpinner /> : null}
      {edges ? (
        <>
          {edges.length ? (
            <>
              <table className={styles.ratingsTable}>
                <tbody>
                  <tr>
                    <th>Company</th>
                    <th>Ticker</th>
                    <th>Direction</th>
                    <th>Return To Date</th>
                    <th>Your Rating</th>
                    <th>Date Rated</th>
                    <th>Return Since Your Rating</th>
                  </tr>
                  {edges.map(edge => {
                    if (!edge?.node) return null;

                    const { id, score, createdAt, totalReturn, idea } = edge.node;

                    return (
                      <tr data-test="rating-row" key={id}>
                        <td>
                          <Link to={ideaUrl(idea.id)} className={styles.ideaLink}>
                            {idea.positions.map(pos => pos?.asset?.name || '').join(' • ')}
                          </Link>
                        </td>
                        <td>
                          <Link to={ideaUrl(idea.id)} className={styles.ideaLink}>
                            {compact(
                              idea.positions.map(pos => pos?.asset?.primarySymbol || '')
                            ).join(' • ')}
                          </Link>
                        </td>
                        <td>
                          <strong className={styles.direction}>{idea.direction}</strong>
                        </td>
                        <td>{renderReturn(idea.totalReturn)}</td>
                        <td>
                          {[0, 1, 2, 3, 4].map(idx => (
                            <span key={idx}>
                              <StarIcon
                                id={`star-${score}-${idx}`}
                                fillPercentage={String(calculateFill(idx, score / 2))}
                              />
                            </span>
                          ))}
                        </td>
                        <td>{formatDate(createdAt)}</td>
                        <td>{renderReturn(totalReturn)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {pageInfo?.hasNextPage && isFetching && <LoadingSpinner />}
            </>
          ) : (
            <div className={styles.noContentMsg}>
              <div>
                Rate ideas to help us make better idea recommendations to you, and to keep a record
                of how your favorite ideas worked out.
              </div>
              <Button
                url={ideasUrl()}
                text="Visit Ideas Page"
                icon={LightbulbIcon}
                type={BUTTON_TYPES.PRIMARY}
              />
            </div>
          )}
        </>
      ) : null}
    </>
  );
};

export default UserRatingsPage;
